import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { X, ArrowRight, PhoneCall, CheckCircle2, ChevronLeft, Loader2 } from "lucide-react";
import { base44 } from "@/api/base44Client";

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function groupSlotsByDate(slots) {
  const groups = {};
  slots.forEach((s) => {
    const d = new Date(s.start);
    const key = d.toDateString();
    if (!groups[key]) groups[key] = [];
    groups[key].push(s);
  });
  return Object.entries(groups).map(([dateKey, daySlots]) => {
    const d = new Date(daySlots[0].start);
    return {
      key: dateKey,
      label: `${DAY_NAMES[d.getDay()]} ${d.getDate()} ${MONTH_NAMES[d.getMonth()]}`,
      slots: daySlots,
    };
  });
}

function formatTime(iso) {
  const d = new Date(iso);
  return d.toLocaleTimeString("en-GB", { hour: "numeric", minute: "2-digit" });
}

export default function BookCallModal({ onClose, onGetValuation }) {
  const [stage, setStage] = useState(0);
  const [slots, setSlots] = useState([]);
  const [eventInfo, setEventInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedDateIdx, setSelectedDateIdx] = useState(0);
  const [openingSlot, setOpeningSlot] = useState(null);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  const fetchSlots = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await base44.functions.invoke("getCalendlySlots", {});
      const data = res.data || res;
      setSlots(data.slots || []);
      setEventInfo(data);
    } catch (err) {
      setError("We couldn't load available times. Please try again or use our scheduling page directly.");
    }
    setLoading(false);
  };

  useEffect(() => {
    if (stage === 1 && slots.length === 0 && !loading && !error) {
      fetchSlots();
    }
  }, [stage]);

  const handleGetValuation = () => {
    onClose();
    if (onGetValuation) onGetValuation();
  };

  const handleSlotClick = (slot) => {
    setOpeningSlot(slot.start);
    window.open(slot.scheduling_url, "_blank");
    setTimeout(() => {
      setOpeningSlot(null);
      setStage(2);
    }, 600);
  };

  const dateGroups = groupSlotsByDate(slots);
  const currentDateGroup = dateGroups[selectedDateIdx];

  return createPortal(
    <div
      className="bc-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: "fixed", inset: 0, zIndex: 9999,
        background: "rgba(0,0,0,0.55)", backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
      }}
    >
      <div
        className="bc-shell"
        style={{
          width: "100%", maxWidth: stage === 1 ? 560 : 460,
          maxHeight: "92vh", background: "#fff", borderRadius: 16,
          boxShadow: "0 24px 80px rgba(0,0,0,0.3)", position: "relative",
          display: "flex", flexDirection: "column", overflow: "hidden",
        }}
      >
        <button
          onClick={onClose}
          style={{ position: "absolute", top: 16, right: 16, zIndex: 10, background: "#F5F5F5", border: "none", borderRadius: "50%", width: 32, height: 32, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
        >
          <X size={16} color="#888" />
        </button>

        {stage === 0 && (
          <div style={{ padding: "44px 40px 36px", textAlign: "center" }}>
            <div style={{ width: 56, height: 56, borderRadius: 14, background: "#E8EDFF", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
              <PhoneCall size={24} color="#4A6DC7" />
            </div>
            <h2 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 24, color: "#1A1A1A", margin: "0 0 12px", letterSpacing: "-0.01em" }}>Book a Call</h2>
            <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 15, color: "#666", lineHeight: 1.6, margin: "0 0 8px" }}>
              Looking for an income estimate or want to join our management service?
            </p>
            <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 15, color: "#666", lineHeight: 1.6, margin: "0 0 28px" }}>
              We recommend completing our <strong style={{ color: "#1A1A1A" }}>Get Valuation</strong> form first. It gives us the property details we need to prepare — and you can book a call right after.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <div>
                <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 10, color: "#999", margin: "0 0 6px", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 600, textAlign: "left" }}>Recommended</p>
                <button
                  onClick={handleGetValuation}
                  style={{ background: "#112215", color: "#fff", border: "none", borderRadius: 10, padding: "15px", fontFamily: "'Inter', sans-serif", fontSize: 15, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, width: "100%", transition: "background 0.2s" }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "#1A3A2A")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "#112215")}
                >
                  Get Valuation <ArrowRight size={16} />
                </button>
              </div>
              <button
                onClick={() => setStage(1)}
                style={{ background: "none", color: "#555", border: "1.5px solid #E5E5E5", borderRadius: 10, padding: "14px", fontFamily: "'Inter', sans-serif", fontSize: 15, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, transition: "all 0.2s" }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#112215"; e.currentTarget.style.color = "#112215"; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#E5E5E5"; e.currentTarget.style.color = "#555"; }}
              >
                Book a Call
              </button>
            </div>
          </div>
        )}

        {stage === 1 && (
          <div style={{ display: "flex", flexDirection: "column", height: "100%", maxHeight: "92vh" }}>
            <div style={{ padding: "16px 20px", borderBottom: "1px solid #F0F0F0", display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
              <button
                onClick={() => setStage(0)}
                style={{ background: "none", border: "none", color: "#888", fontFamily: "'Inter', sans-serif", fontSize: 14, cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}
              >
                <ChevronLeft size={16} /> Back
              </button>
              <h3 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 15, color: "#1A1A1A", margin: 0 }}>
                {eventInfo ? `${eventInfo.eventTypeName} · ${eventInfo.eventDuration} min` : "Pick a time"}
              </h3>
            </div>

            <div style={{ flex: 1, overflow: "auto", padding: "20px" }}>
              {loading && (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "40px 0", gap: 12 }}>
                  <Loader2 size={28} color="#1A1A1A" className="animate-spin" style={{ animation: "bc-spin 0.8s linear infinite" }} />
                  <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 14, color: "#888", margin: 0 }}>Loading available times…</p>
                </div>
              )}

              {error && !loading && (
                <div style={{ textAlign: "center", padding: "32px 12px" }}>
                  <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 14, color: "#666", margin: "0 0 16px", lineHeight: 1.5 }}>{error}</p>
                  <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
                    <button onClick={fetchSlots} style={{ background: "#112215", color: "#fff", border: "none", borderRadius: 8, padding: "10px 20px", fontFamily: "'Inter', sans-serif", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>Retry</button>
                    {eventInfo?.schedulingUrl && (
                      <a href={eventInfo.schedulingUrl} target="_blank" rel="noopener noreferrer" style={{ background: "none", color: "#555", border: "1.5px solid #E5E5E5", borderRadius: 8, padding: "10px 20px", fontFamily: "'Inter', sans-serif", fontSize: 14, fontWeight: 600, textDecoration: "none" }}>Open Calendly</a>
                    )}
                  </div>
                </div>
              )}

              {!loading && !error && slots.length === 0 && (
                <div style={{ textAlign: "center", padding: "32px 12px" }}>
                  <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 15, color: "#666", margin: "0 0 16px", lineHeight: 1.5 }}>No times available in the next two weeks. Please email us or try our scheduling page.</p>
                  {eventInfo?.schedulingUrl && (
                    <a href={eventInfo.schedulingUrl} target="_blank" rel="noopener noreferrer" style={{ display: "inline-block", background: "#112215", color: "#fff", borderRadius: 8, padding: "12px 24px", fontFamily: "'Inter', sans-serif", fontSize: 14, fontWeight: 600, textDecoration: "none" }}>Open Calendly</a>
                  )}
                </div>
              )}

              {!loading && !error && slots.length > 0 && (
                <>
                  {/* Date selector */}
                  <div className="bc-dates" style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 16, borderBottom: "1px solid #F0F0F0", marginBottom: 16, WebkitOverflowScrolling: "touch" }}>
                    {dateGroups.map((group, idx) => {
                      const sel = idx === selectedDateIdx;
                      return (
                        <button
                          key={group.key}
                          onClick={() => setSelectedDateIdx(idx)}
                          style={{
                            flexShrink: 0, padding: "10px 14px", borderRadius: 10,
                            border: sel ? "2px solid #1A1A1A" : "1.5px solid #E5E5E5",
                            background: sel ? "#1A1A1A" : "#fff",
                            color: sel ? "#fff" : "#555",
                            fontFamily: "'Inter', sans-serif", fontSize: 13, fontWeight: 600,
                            cursor: "pointer", transition: "all 0.15s", whiteSpace: "nowrap",
                          }}
                        >
                          {group.label}
                        </button>
                      );
                    })}
                  </div>

                  {/* Time slots */}
                  <div className="bc-times" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(90px, 1fr))", gap: 8 }}>
                    {currentDateGroup?.slots.map((slot) => {
                      const isOpening = openingSlot === slot.start;
                      return (
                        <button
                          key={slot.start}
                          onClick={() => handleSlotClick(slot)}
                          disabled={isOpening}
                          style={{
                            padding: "11px 8px", borderRadius: 8,
                            border: "1.5px solid #E5E5E5", background: "#fff",
                            color: "#1A1A1A",
                            fontFamily: "'Inter', sans-serif", fontSize: 14, fontWeight: 600,
                            cursor: "pointer", transition: "all 0.15s",
                            display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                          }}
                          onMouseEnter={(e) => { if (!isOpening) { e.currentTarget.style.borderColor = "#112215"; e.currentTarget.style.background = "#FAFAFA"; } }}
                          onMouseLeave={(e) => { if (!isOpening) { e.currentTarget.style.borderColor = "#E5E5E5"; e.currentTarget.style.background = "#fff"; } }}
                        >
                          {isOpening && <Loader2 size={13} style={{ animation: "bc-spin 0.8s linear infinite" }} />}
                          {formatTime(slot.start)}
                        </button>
                      );
                    })}
                  </div>
                  <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: "#999", margin: "16px 0 0", textAlign: "center" }}>
                    Times shown in your local timezone. Click a slot to confirm on Calendly.
                  </p>
                </>
              )}
            </div>
          </div>
        )}

        {stage === 2 && (
          <div style={{ padding: "44px 40px 36px", textAlign: "center" }}>
            <div style={{ width: 56, height: 56, borderRadius: "50%", background: "#F0FDF4", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
              <CheckCircle2 size={28} color="#22c55e" />
            </div>
            <h2 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 24, color: "#1A1A1A", margin: "0 0 12px", letterSpacing: "-0.01em" }}>Almost there!</h2>
            <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 15, color: "#666", lineHeight: 1.6, margin: "0 0 28px" }}>
              We've opened Calendly in a new tab to confirm your booking. To help us prepare, we'd still recommend completing a quick valuation form with your property details.
            </p>
            <button
              onClick={handleGetValuation}
              style={{ background: "#112215", color: "#fff", border: "none", borderRadius: 10, padding: "15px", fontFamily: "'Inter', sans-serif", fontSize: 15, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, width: "100%", transition: "background 0.2s" }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "#1A3A2A")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "#112215")}
            >
              Complete Valuation Form <ArrowRight size={16} />
            </button>
            <button
              onClick={onClose}
              style={{ background: "none", border: "none", color: "#999", fontFamily: "'Inter', sans-serif", fontSize: 14, cursor: "pointer", marginTop: 12, padding: 8 }}
            >
              Maybe later
            </button>
          </div>
        )}
      </div>

      <style>{`
        @keyframes bc-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @media (max-width: 600px) {
          .bc-overlay { padding: 0 !important; }
          .bc-shell { max-width: 100% !important; width: 100% !important; max-height: 100dvh !important; height: 100dvh !important; border-radius: 0 !important; }
        }
      `}</style>
    </div>,
    document.body
  );
}